import { TemplatePortal } from '@angular/cdk/portal';
import type { StepperOptions } from '@angular/cdk/stepper';
import { CdkStep, STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { ViewContainerRef, type AfterContentInit, type OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  forwardRef,
  ContentChild,
  Optional,
} from '@angular/core';
import { Subscription, map, startWith, switchMap } from 'rxjs';
import { PalantirTabContentDirective } from '../../directives/content.directive';
import { PalantirStepLabel } from '../../directives/step-label.directive';
import { PalantirTabsComponent } from '../../tabs.component';

@Component({
  standalone: false,
  selector: 'palantir-tab',
  templateUrl: './tab.component.html',
  providers: [{ provide: CdkStep, useExisting: PalantirTabComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PalantirTabComponent extends CdkStep implements AfterContentInit, OnDestroy {
  @ContentChild(PalantirStepLabel) public override stepLabel: PalantirStepLabel = undefined!;

  @ContentChild(PalantirTabContentDirective, { static: false })
  public _lazyContent?: PalantirTabContentDirective;

  public _portal!: TemplatePortal;

  private _isSelected = Subscription.EMPTY;

  public constructor(
    @Inject(forwardRef(() => PalantirTabsComponent)) tabs: PalantirTabsComponent,
    @Optional() @Inject(STEPPER_GLOBAL_OPTIONS) stepperOptions?: StepperOptions,
    private readonly _viewContainerRef?: ViewContainerRef,
  ) {
    super(tabs, stepperOptions);
  }

  public ngAfterContentInit() {
    if (this._viewContainerRef) {
      this._isSelected = this._stepper.steps.changes
        .pipe(
          switchMap(() =>
            this._stepper.selectionChange.pipe(
              map((event) => event.selectedStep === this),
              startWith(this._stepper.selected === this),
            ),
          ),
        )
        .subscribe((isSelected) => {
          if (isSelected && this._lazyContent && !this._portal) {
            this._portal = new TemplatePortal(this._lazyContent._template, this._viewContainerRef!);
          }
        });
    }
  }

  public ngOnDestroy() {
    this._isSelected.unsubscribe();
  }
}
